import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { StevensTopbar } from '@/components/landing/StevensTopbar';
import { StevensFooter } from '@/components/landing/StevensFooter';
import { MonoLabel } from '@/components/editorial';
import { SkipToContent } from '@/components/a11y/SkipToContent';
import { MachineTranslationBanner } from '@/components/layout/MachineTranslationBanner';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import '@/pages/landing-v2.css';

interface AsideMetaRow {
  k: string;
  v: ReactNode;
}

interface AsideAlternate {
  prompt: string;
  to: string;
  label: string;
}

interface AuthShellProps {
  documentLabel: string;
  meta?: AsideMetaRow[];
  alternate?: AsideAlternate;
  /**
   * A message the visitor must see first (for example "your session ended").
   * It leads the page, above the language switcher and the article, so it is
   * the first thing read at every width.
   */
  notice?: ReactNode;
  /**
   * `centered` (the sign-in page, Victor 2026-10-21): one narrow column in
   * the middle of the page with no aside, and a top bar without Sign in or
   * Apply whose language switcher is the icon alone. `documentLabel`, `meta` and `alternate` are not drawn.
   */
  layout?: 'document' | 'centered';
  children: ReactNode;
}

export function AuthShell({
  documentLabel,
  meta = [],
  alternate,
  notice,
  layout = 'document',
  children,
}: AuthShellProps) {
  const { t } = useTranslation('shell');
  if (layout === 'centered') {
    return (
      <div className="landing-page">
        <SkipToContent />
        <StevensTopbar showApply={false} showSignIn={false} compactLanguage />
        <MachineTranslationBanner />
        <main id="main-content" className="wrap">
          <div className="mx-auto flex w-full max-w-[400px] flex-col gap-8 py-14 md:py-20">
            {notice}
            <article>{children}</article>
          </div>
        </main>
        <StevensFooter />
      </div>
    );
  }
  return (
    <div className="landing-page">
      <SkipToContent />
      <StevensTopbar />
      {/* #2109 R1 (L4): the language switcher is in the topbar (Victor
          2026-10-20), so every auth page — Login, ForgotPassword,
          ResetPassword, MagicLink, Mfa, ProviderUpload — has it at the top;
          the banner says when the language is machine-translated. */}
      <MachineTranslationBanner />
      <main id="main-content" className="wrap">
        {/* L4-a (launch-w1014): a family on a phone reads the headline and the
            form first. The grid is one column below md, so DOM order is
            reading order: notice, article, then the aside ("Document",
            "Est. time", "Save anytime", "Already started?"). At md and up the
            aside heads the left column and the article sits on the right. */}
        <div
          className={cn(
            'grid grid-cols-1 gap-10 py-14 md:grid-cols-[220px_1fr] md:gap-x-16 md:gap-y-5 md:py-20',
            notice ? 'md:grid-rows-[auto_1fr]' : 'md:grid-rows-[1fr]',
          )}
        >
          {notice ? <div className="md:col-span-2 md:mb-11">{notice}</div> : null}
          <article
            className={cn(
              'max-w-[640px] md:col-start-2',
              notice ? 'md:row-start-2' : 'md:row-start-1',
            )}
          >
            {children}
          </article>

          <aside
            className={cn(
              'flex flex-col gap-5 text-body-sm text-stevens-ink-2 md:col-start-1 md:sticky md:top-24 md:self-start',
              notice ? 'md:row-start-2' : 'md:row-start-1',
            )}
          >
            <div className="flex flex-col gap-0.5">
              <MonoLabel>{t('authShell.document')}</MonoLabel>
              <span className="text-body text-stevens-ink">{documentLabel}</span>
            </div>
            {meta.map((row) => (
              <div key={row.k} className="flex flex-col gap-0.5">
                <MonoLabel>{row.k}</MonoLabel>
                <span className="text-body text-stevens-ink">{row.v}</span>
              </div>
            ))}
            {alternate ? (
              <div className="mt-4 flex flex-col gap-1.5 border-t border-stevens-rule-2 pt-5">
                <MonoLabel>{alternate.prompt}</MonoLabel>
                <Link
                  to={alternate.to}
                  className="text-body text-stevens-ink hover:text-stevens-indigo border-b border-stevens-rule pb-0.5 self-start transition-colors"
                >
                  {alternate.label} {"→"}
                </Link>
              </div>
            ) : null}
          </aside>
        </div>
      </main>
      <StevensFooter />
    </div>
  );
}
